/**
 * Sanitize a value for safe interpolation inside a JavaScript string
 * literal within dangerouslySetInnerHTML. Uses a strict allowlist to
 * strip anything that could break out of the string context.
 */
export const sanitizeForScript = (value: string | null | undefined): string => {
  if (!value) return ""
  return value.replace(/[^a-zA-Z0-9_\-.:/= &?,@#%+()]/g, "")
}

/**
 * Serialise a value as a complete JavaScript literal — a quoted string, or
 * the bare token `null` when absent — for interpolation into an inline
 * `<script>`. Unlike `sanitizeForScript` this preserves the value, so use
 * it for anything a human reads back: city names, user agents.
 *
 * `JSON.stringify` handles the JavaScript-string context (quotes,
 * backslashes, newlines). Escaping `<` handles the HTML one: a value
 * containing `</script>` closes the element early no matter how well the
 * JavaScript inside it is quoted, because the HTML parser never gets far
 * enough to care.
 *
 * @param value - Raw value. Numbers are emitted as quoted strings, matching
 *   the shape the tracking payloads already send.
 * @returns A JavaScript literal, safe to drop straight into script source.
 */
export const toScriptLiteral = (
  value: string | number | null | undefined
): string => {
  if (value === null || value === undefined || value === "") {
    return "null"
  }

  return JSON.stringify(String(value)).replace(/</g, "\\u003c")
}
